import React, { useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { AxiosError } from 'axios';
import OrganisationInput from './components/OrganisationInput';
import {
    Box,
    Button,
    Card,
    Container,
    Link,
    Typography,
    useTheme,
    useMediaQuery,
    IconButton,
    Alert,
} from '@mui/material';
import IconTextField from '../../common/components/IconTextfield.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye,
    faEyeSlash,
    faLock,
    faUser,
} from '@fortawesome/free-solid-svg-icons';
import { NavigationRoutes } from '../../application/router/routes.ts';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { faUnlock } from '@fortawesome/free-solid-svg-icons/faUnlock';
import logo from '../../assets/logmed_logo.png';
import { useEffect } from 'react';
import { useGlobalStore } from '../../application/store/store.tsx';
import SnackbarComponent from '../../common/components/SnackbarComponent.tsx';

export const LoginView = () => {
    const setMessage = useGlobalStore((state) => state.setMessage);
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const message = useGlobalStore((state) => state.message);
    const messageType = useGlobalStore((state) => state.messageType);
    const isMessageVisible = useGlobalStore((state) => state.isMessageVisible);
    const clearMessage = useGlobalStore((state) => state.clearMessage);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const sessionExpired = searchParams.get('sessionExpired') === 'true';
    const [, setOrgIdIsSet] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { login } = useAuth();
    const [changeOrgId, setChangeOrgId] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = React.useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
    };
    const handleMouseUpPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
    };

    // Responsive design
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await login(username, password);
        } catch (e) {
            if (e instanceof AxiosError) {
                if (
                    e.response?.status === 401 &&
                    e.response.data?.exception === 'Authentication Failed.'
                ) {
                    setErrorMessage('Inloggningen Misslyckades.');
                } else {
                    setErrorMessage(e.response.data?.exception);
                }
            }
            setShowErrorMessage(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (location.state?.message) {
            setMessage(location.state.message, location.state.type);
            window.history.replaceState({}, document.title);
        }
    }, []);

    /*
    const handleIdentityProviderLogin = async () => {
        await userManager.signinRedirect();
    };
     */

    // Check if tenant (orgId) is set - this is required for multi-tenant login
    if (localStorage.getItem('orgId') === null || changeOrgId) {
        return (
            <OrganisationInput
                onOrgIdSaved={() => {
                    setChangeOrgId(false);
                    setOrgIdIsSet(true);
                }}
            />
        );
    }

    // Login form content
    const loginFormContent = (
        <>
            {sessionExpired && (
                <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                    Sessionen har gått ut, vänligen logga in igen
                </Alert>
            )}
            <Typography
                component="h1"
                variant="h5"
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
            >
                Logga in
            </Typography>
            <Box
                component="form"
                onSubmit={handleLogin}
                sx={{ mt: 1, width: '100%' }}
            >
                <IconTextField
                    size="medium"
                    prefix={
                        <FontAwesomeIcon
                            icon={faUser}
                            className="text-gray-400"
                        />
                    }
                    margin="normal"
                    required
                    fullWidth
                    name="username"
                    placeholder="Användarnamn"
                    id="username"
                    autoComplete="off"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                />
                <IconTextField
                    size="medium"
                    prefix={
                        <FontAwesomeIcon
                            icon={faLock}
                            className="text-gray-400"
                        />
                    }
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    placeholder="Lösenord"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="off"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    suffix={
                        <IconButton
                            aria-label={
                                showPassword
                                    ? 'hide the password'
                                    : 'display the password'
                            }
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            onMouseUp={handleMouseUpPassword}
                            edge="end"
                        >
                            <FontAwesomeIcon
                                icon={showPassword ? faEyeSlash : faEye}
                                className="text-gray-400"
                            />
                        </IconButton>
                    }
                />
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    startIcon={
                        isLoading ? (
                            <CircularProgress size={24} color="info" />
                        ) : (
                            <FontAwesomeIcon icon={faUnlock} />
                        )
                    }
                    sx={{ mt: 3, mb: 2, py: { xs: 1.5, sm: 2 } }}
                >
                    Logga in
                </Button>
                <Link
                    href="#"
                    variant="body2"
                    onClick={() => setChangeOrgId(true)}
                    sx={{
                        display: 'block',
                        textAlign: 'center',
                        mt: 2,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        padding: { xs: '8px 0', sm: 0 },
                    }}
                >
                    Ändra kund-ID
                </Link>
                {showErrorMessage && (
                    <Typography
                        color="error"
                        align="center"
                        sx={{
                            mt: 2,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            padding: { xs: '8px', sm: 0 },
                        }}
                    >
                        {errorMessage}
                    </Typography>
                )}
            </Box>
            <Link
                href="#"
                variant="body2"
                onClick={() => navigate(NavigationRoutes.forgottenPassword)}
                sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 2,
                    mb: { xs: 1, sm: 0 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 0', sm: 0 },
                }}
            >
                Jag har glömt mitt lösenord
            </Link>
        </>
    );

    return (
        <Container component="main" maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
            <SnackbarComponent
                open={isMessageVisible}
                message={message}
                severity={messageType}
                onClose={clearMessage}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: { xs: 2, sm: 4 },
                }}
            >
                <img
                    src={logo}
                    alt="Logmed Logo"
                    style={{
                        maxWidth: isMobile ? '60%' : '80%',
                        height: 'auto',
                    }}
                />
            </Box>
            {isMobile ? (
                // Mobile version without Card
                <Box
                    sx={{
                        marginTop: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {loginFormContent}
                </Box>
            ) : (
                // Desktop version with Card
                <Card
                    sx={{
                        marginTop: 5,
                        padding: { sm: 4, md: 8 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    {loginFormContent}
                </Card>
            )}
        </Container>
    );
};
